import { useState } from 'react';
import { Dropdown, DropdownItemProps } from 'semantic-ui-react';

/** Semantic UI multi Dropdown sometimes emits a non-array; API expects string[]. */
function coerceMultiSelectValue(next: unknown): string[] {
  if (Array.isArray(next)) {
    return next.map(String);
  }
  if (next == null || next === '') {
    return [];
  }
  if (typeof next === 'object') {
    return Object.values(next as Record<string, unknown>).map(String);
  }
  return [String(next)];
}

function formatMultiDisplay(
  value: unknown,
  options: DropdownItemProps[],
  defaultValue: unknown
): string {
  if (!Array.isArray(value) || value.length === 0) {
    return defaultValue != null ? String(defaultValue) : '—';
  }
  const orderMap = new Map(options.map((o, i) => [String(o.value), i]));
  const sorted = [...value].sort(
    (a, b) => (orderMap.get(String(a)) ?? 99) - (orderMap.get(String(b)) ?? 99)
  );
  return sorted
    .map(v => options.find(o => o.value === v)?.text ?? String(v))
    .join(', ');
}

export default function EditableCell({
  value,
  defaultValue,
  options,
  onChange,
  disabled,
  multiple = false
}: {
  value: any
  defaultValue?: any
  options?: DropdownItemProps[]
  onChange?: (value: any) => void
  disabled?: boolean
  multiple?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing && options) {
    if (multiple) {
      return (
        <Dropdown
          fluid
          multiple
          search
          selection
          options={options}
          defaultOpen
          value={Array.isArray(value) ? value : []}
          onClose={() => setIsEditing(false)}
          onChange={(e, { value: next }) => onChange?.(coerceMultiSelectValue(next))}
          selectOnNavigation={false}
          selectOnBlur={false}
        />
      );
    }
    return (
      <Dropdown
        fluid
        search
        selection
        options={options}
        defaultOpen
        defaultValue={value ?? null}
        onClose={() => setIsEditing(false)}
        onChange={(e, { value }) => onChange?.(value)}
        selectOnNavigation={false}
        selectOnBlur={false}
      />
    );
  }

  const displayValue = multiple
    ? formatMultiDisplay(value, options ?? [], defaultValue)
    : (options && value && options.find((option) => option.value === value)?.text)
        ?? value
        ?? defaultValue;

  return (
    <button
      disabled={disabled}
      style={{
        all: 'unset',
        transition: 'background-color 0.2s',
        boxSizing: 'border-box',
        width: '100%',
        height: '100%',
        borderRadius: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '2.25rem',
        paddingLeft: '0.75rem',
        paddingRight: '0.75rem',
      }}
      onMouseOver={(e: any) => {
        e.currentTarget.style.background = 'var(--color-grey)';
        e.currentTarget.style.cursor = 'pointer';
      }}
      onMouseOut={(e: any) => e.currentTarget.style.background = 'initial'}
      onFocus={(e: any) => e.currentTarget.style.background = 'var(--color-grey)'}
      onBlur={(e: any) => e.currentTarget.style.background = 'initial'}
      onClick={() => setIsEditing(true)}
    >
      {displayValue}
    </button>
  );
}